import { Eye, EyeOff } from "lucide-react";
import { useId, useState } from "react";

import { cn } from "../lib/utils";

// A labelled password input with an accessible show/hide toggle. Only the field's `type` changes
// (password <-> text), so password managers, autofill, and normal keyboard/mobile behaviour keep
// working. The value is controlled by the caller and is never stored here.
export function PasswordField({
  id,
  label,
  value,
  autoComplete,
  onChange,
  className,
}: {
  id?: string;
  label: string;
  value: string;
  autoComplete: "current-password" | "new-password";
  onChange: (value: string) => void;
  className?: string;
}) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const [visible, setVisible] = useState(false);

  return (
    <div className={cn("text-sm font-medium text-foreground", className)}>
      <label htmlFor={inputId}>{label}</label>
      <span className="relative mt-1 block">
        <input
          id={inputId}
          type={visible ? "text" : "password"}
          autoComplete={autoComplete}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="w-full rounded-xl border border-border bg-white px-3 py-2.5 pr-11 text-sm text-foreground shadow-soft focus:border-primary/40 focus:outline-none focus:ring-4 focus:ring-primary/10"
        />
        <button
          type="button"
          onClick={() => setVisible((current) => !current)}
          aria-label={visible ? "Hide password" : "Show password"}
          aria-pressed={visible}
          className="absolute inset-y-0 right-0 grid w-11 place-items-center rounded-r-xl text-muted-foreground transition hover:text-foreground focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/10"
        >
          {visible ? <EyeOff className="size-4" aria-hidden="true" /> : <Eye className="size-4" aria-hidden="true" />}
        </button>
      </span>
    </div>
  );
}
